// Hook personalizado para Analytics (Google Analytics + Umami)
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import combinedAnalyticsService from '../services/combinedAnalytics';

// Hook para rastreamento automático de páginas
export const usePageTracking = () => {
  const location = useLocation();
  const prevLocation = useRef();

  useEffect(() => {
    // Evitar rastreamento duplicado na primeira renderização
    if (prevLocation.current !== undefined) {
      const pagePath = location.pathname + location.search;
      const pageTitle = document.title;
      
      combinedAnalyticsService.trackPageView(pagePath, pageTitle);
    }
    
    prevLocation.current = location.pathname;
  }, [location]);
};

// Hook para rastreamento de tempo de sessão
export const useSessionTracking = () => {
  const sessionStart = useRef(Date.now());
  const isTracked = useRef(false);

  useEffect(() => {
    const trackSession = () => {
      if (!isTracked.current) {
        const sessionDuration = Date.now() - sessionStart.current;
        combinedAnalyticsService.trackSessionDuration(sessionDuration);
        isTracked.current = true;
      }
    };

    // Rastrear quando o usuário sair da página
    const handleBeforeUnload = () => {
      trackSession();
    };

    // Rastrear quando a página perder o foco
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      trackSession();
    };
  }, []);
};

// Hook para rastreamento de erros
export const useErrorTracking = () => {
  useEffect(() => {
    const handleError = (event) => {
      combinedAnalyticsService.trackError('javascript_error', event.error?.message || 'Erro desconhecido');
    };

    const handleUnhandledRejection = (event) => {
      combinedAnalyticsService.trackError('promise_rejection', event.reason?.message || 'Promise rejeitada');
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
};

// Hook principal que combina todos os rastreamentos
export const useAnalytics = (options = {}) => {
  // Sempre chama os hooks para seguir as regras do React
  usePageTracking();
  useSessionTracking();
  useErrorTracking();

  return combinedAnalyticsService;
};

export default useAnalytics;